import React, { useRef, useState } from 'react';
import { transformText, limitInput, INPUT_LIMIT, BIG_INPUT_LIMIT } from '../../lib/campaignTransforms';
import '../../styles/campaign.css';

const BOLD = [['serifBold', 'Serif bold'], ['italic', 'Serif italic'], ['boldItalic', 'Serif bold italic'], ['sansBold', 'Sans bold'], ['sansItalic', 'Sans italic'], ['sansBoldItalic', 'Sans bold italic']];
const BUBBLE = [['circled', 'Open bubbles'], ['circledFilled', 'Filled bubbles']];
const ALPHABET = [['script', 'Cursive script'], ['boldScript', 'Bold cursive script']];
const MODES = {
  fancy: [...BOLD, ...ALPHABET, ...BUBBLE, ['monospace', 'Monospace'], ['doubleStruck', 'Double-struck']],
  bold: BOLD, bubble: BUBBLE, alphabet: ALPHABET,
  big: [['ascii', 'ASCII block lettering'], ['fullwidth', 'Fullwidth Unicode'], ['squared', 'Squared Unicode']],
  reverse: [['reverse', 'Reverse order'], ['upsideDown', 'Upside-down approximation'], ['mirror', 'Approximate mirror']],
  strike: [['strike', 'Strikethrough']],
};
const SYMBOLS = [
  ['♡', 'White heart', 'hearts'], ['♥', 'Black heart suit', 'hearts'], ['❤', 'Heavy black heart', 'hearts'],
  ['☆', 'White star', 'stars'], ['★', 'Black star', 'stars'], ['✦', 'Four-pointed star', 'stars'], ['✧', 'White four-pointed star', 'stars'],
  ['·', 'Middle dot', 'separators'], ['•', 'Bullet', 'separators'], ['|', 'Vertical line', 'separators'], ['—', 'Em dash', 'separators'],
  ['→', 'Rightwards arrow', 'arrows'], ['←', 'Leftwards arrow', 'arrows'], ['↔', 'Left right arrow', 'arrows'],
];
const COMPOSITIONS = [['♡', 'Heart frame'], ['✦', 'Star frame'], ['·', 'Minimal separator']];
function CopyResult({ label, value, ascii = false, compact = false, onNotice }) {
  const output = useRef(null);
  return <div className="tool-card campaign-result"><h3>{label}</h3>
    <textarea ref={output} className={ascii ? 'campaign-ascii' : ''} rows={ascii ? 6 : compact ? 1 : 3} wrap={ascii ? 'off' : 'soft'} aria-label={`${label} result`} readOnly value={value} />
    <button className="copy-btn" type="button" disabled={!value} onClick={async () => {
      try {
        await navigator.clipboard.writeText(value);
        onNotice(`Copied ${label}.`);
      } catch {
        output.current?.focus(); output.current?.select();
        onNotice('Copy failed. The result is selected: copy it manually.');
      }
    }}>Copy {label}</button>
  </div>;
}
export default function StylePicker({ mode = 'fancy', initialText = 'Hello world' }) {
  if (typeof Intl.Segmenter !== 'function') return <section className="tool campaign-picker">
    <p role="status">Grapheme-safe conversion is unavailable in this browser. Your original text is preserved below. Try an updated browser.</p>
    <label>Original text<textarea aria-label="Original text" readOnly value={initialText} /></label>
  </section>;
  return <SupportedStylePicker mode={mode} initialText={initialText} />;
}
function SupportedStylePicker({ mode, initialText }) {
  const limit = mode === 'big' ? BIG_INPUT_LIMIT : INPUT_LIMIT;
  const [text, setText] = useState(() => limitInput(initialText, limit));
  const [notice, setNotice] = useState(() => initialText !== limitInput(initialText, limit) ? 'Input shortened to the tool limit.' : '');
  const [filter, setFilter] = useState('');
  const [symbol, setSymbol] = useState('♡');
  const isSymbols = mode === 'symbols';
  const isCompositions = mode === 'symbol-bios';
  const styles = isSymbols || isCompositions ? [] : MODES[mode] || MODES.fancy;
  const visible = styles.filter(([, label]) => label.toLowerCase().includes(filter.toLowerCase()));
  const symbols = SYMBOLS.filter(([, label, group]) => `${label} ${group}`.toLowerCase().includes(filter.toLowerCase()));
  return <section className="tool campaign-picker" aria-label="Text style picker">
    <label>Your text<textarea aria-label="Your text" value={text} onChange={e => {
      const next = limitInput(e.target.value, limit);
      setNotice(next !== e.target.value ? 'Input shortened to the tool limit.' : ''); setText(next);
    }} /></label>
    <p className="ctrl-label">Up to {limit} characters (complete graphemes), with an 8000 code-unit safety cap.</p>
    <p role="status">{notice}</p>
    {!isCompositions && <label>{isSymbols ? 'Find symbols' : 'Filter styles'}<input aria-label={isSymbols ? 'Find symbols' : 'Filter styles'} value={filter} onChange={e => setFilter(e.target.value)} /></label>}
    {!isSymbols && !isCompositions && !visible.length && <p>No styles match. Try bold, script or bubbles, or clear the filter.</p>}
    {visible.map(([id, label]) => <CopyResult key={id} label={label} ascii={id === 'ascii'} value={transformText(limitInput(text, limit), id)} onNotice={setNotice} />)}
    {mode === 'alphabet' && <div className="campaign-letter-grid" aria-label="Cursive alphabet letters">{Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz').map(letter =>
      <div key={letter} data-alphabet-letter={letter}><CopyResult compact label={`${letter === letter.toUpperCase() ? 'Uppercase' : 'Lowercase'} ${letter}`} value={transformText(letter, 'script')} onNotice={setNotice} /></div>)}</div>}
    {isSymbols && <>
      <CopyResult label="Phrase preview" value={text ? `${symbol} ${text} ${symbol}` : symbol} onNotice={setNotice} />
      {!symbols.length && <p>No symbols match. Try heart, star, separator or arrow.</p>}
      <div className="campaign-symbol-grid">{symbols.map(([character, label]) => <div key={character}>
        <CopyResult compact label={label} value={character} onNotice={setNotice} />
        <button className="sm-btn" type="button" onClick={() => { setSymbol(character); setNotice(''); }}>Use {label}</button>
      </div>)}</div>
    </>}
    {isCompositions && COMPOSITIONS.map(([character, label]) => <CopyResult key={label} label={label} value={text ? `${character} ${text} ${character}` : ''} onNotice={setNotice} />)}
  </section>;
}
